import { useSelector, useDispatch } from "react-redux"
import { useNavigate, useLocation } from "react-router-dom"
import { useState, forwardRef, lazy, Suspense } from "react"
import { Menu, MenuItem, Tooltip, ListItemIcon, ListItemText, Stack, Dialog, Grow, IconButton, Typography, useTheme, useMediaQuery } from "@mui/material"
import { IoMdMore } from "react-icons/io";
import { BsBookmarkStar } from "react-icons/bs";
import { TbPin, TbTrashX } from "react-icons/tb";
import { PiCircleBold, PiDiamondBold, PiStarBold } from "react-icons/pi";
import { setTriggerRefresh } from "state";
import RowBox from "./RowBox";
import { useMutation, useQueryClient } from "@tanstack/react-query"
import GridLoader from "react-spinners/GridLoader"
import apiUrl from "config/api"
const ViewVocabDialog = lazy(() => import("./ViewVocabDialog"))
const DeleteVocabDialog = lazy(() => import("./DeleteVocabDialog"))

const Transition = forwardRef(function Transition(props, ref) {
  return <Grow ref={ref} {...props} />
})

const PinnedVocabRow = ({ id, text, pinyin, label, difficulty, definition, similar, expression, sentence, pinned }) => {
  const isQHDScreens = useMediaQuery("(min-width:2500px) and (max-height:1600px)") // 2K Laptops
  const isWideScreens = useMediaQuery("(min-width:3400px) and (max-height:1500px)") // Wide and Ultrawide Desktops
  const isLandscape = window.matchMedia("(orientation: landscape)").matches;
  const isPortrait = window.matchMedia("(orientation: portrait)").matches;
  const mode = useSelector((state) => state.mode)
  const token = useSelector((state) => state.token)
  const refreshKey = useSelector((state) => state.refreshKey)
  const triggerRefresh = useSelector((state) => state.triggerRefresh)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const theme = useTheme()
  const queryClient = useQueryClient()

  const [viewVocab, setViewVocab] = useState(false)
  const [viewUsage, setViewUsage] = useState(false)

  /* View Vocab Dialog State */
  const [vocabOpen, setVocabOpen] = useState(false)
  const handleViewOpen = () => {
    if (isPortrait) {
      navigate(location.pathname, { replace: true }) // Prevents unexpected nav back on mobile
    }
    setVocabOpen(true)
    setViewVocab(true)
  }
  const handleViewClose = () => {
    setVocabOpen(false)
    // trigger refresh to recalculate line height cache
    if (triggerRefresh === true) {
      dispatch(setTriggerRefresh({ triggerRefresh: false }))
      setTimeout(() => {
        navigate(0)
      }, 150);
    }
  }

  /* Set Custom Behavior for Back Nav on Mobile */
  function handleMobileBack() {
    setVocabOpen(false)
  }

  function overrideBackNavigation(customBackFunction) {
    if (isPortrait) {
      // Push a dummy state to the history stack to catch the back button
      window.history.pushState(null, null, window.location.href);

      // Listen to the popstate event, which is triggered when the active history entry changes
      window.addEventListener('popstate', (event) => {
        // Call the custom function instead of navigating back
        customBackFunction();

        // Push the dummy state back into history to keep intercepting the back navigation
        window.history.pushState(null, null, window.location.href);
      });
    }
  }

  overrideBackNavigation(() => {
    if (isPortrait) {
      handleMobileBack(); // Call the function handling the Escape key logic
    } else {
      window.history.back()
    }
  });

  function HighlightCaps({ text }) {
    const theme = useTheme();
    const mode = useSelector((state) => state.mode);

    // Function to process the string
    const processText = (text) => {
      // Regular expression to match consecutive 2 or more uppercase letters, text inside parentheses, or text inside double quotes
      const regex = /[A-Z]{2,}|\(.*?\)|".*?"/g;

      // Split the text based on the match
      const parts = text.split(regex);

      // Find the matches (consecutive capital letters, text inside parentheses, and text inside double quotes)
      const matches = text.match(regex);

      // If no matches found, return the original text
      if (!matches) {
        return text;
      }

      return parts.reduce((acc, part, index) => {
        acc.push(<span key={`part-${index}`}>{part}</span>); // Add normal text
        if (matches[index]) {
          // If there's a match at this index, add it with the highlighted color
          acc.push(
            <span key={`match-${index}`}
              style={{ color: mode === "light" ? "rgb(0 213 143)" : theme.palette.primary.dark }}
            >
              {matches[index]}
            </span>
          );
        }
        return acc;
      }, []);
    };

    return <div>{processText(text)}</div>;
  }

  /* Options Drowndown Menu */
  const [highlightRow, setHighlightRow] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const openMenu = Boolean(menuAnchor)
  const handleMenuClick = (event) => {
    setHighlightRow(true)
    setMenuAnchor(event.currentTarget)
  }
  const handleMenuClose = () => {
    setHighlightRow(false)
    setMenuAnchor(null)
  }

  /* View Vocab Dialog State */
  const [deleteOpen, setDeleteOpen] = useState(false)
  const handleDeleteClose = () => {
    setDeleteOpen(false)
  }
  const handleDeleteOpen = () => {
    handleMenuClose()
    setDeleteOpen(true)
  }

  const handleUpdatePinned = (updatedData) => {
    updatePinnedMutation.mutate(updatedData)
  }

  const updatePinnedMutation = useMutation({
    mutationFn: async () => {
      const updatedData = {
        pinned: !pinned
      }
      return await fetch(`${apiUrl}/vocabs/${id}/update`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      })
    },
    onError: (error, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
      setMenuAnchor(null)
      setHighlightRow(false)
      // setTimeout(() => {
      //   navigate(0)
      // }, 200);
    }
  })

  const handleSetBeg = (updatedData) => {
    setBegMutation.mutate(updatedData)
  }

  const setBegMutation = useMutation({
    mutationFn: async () => {
      const updatedData = {
        difficulty: "1"
      }
      return await fetch(`${apiUrl}/vocabs/${id}/update`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      })
    },
    onError: (error, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
    }
  })

  const handleSetInt = (updatedData) => {
    setIntMutation.mutate(updatedData)
  }

  const setIntMutation = useMutation({
    mutationFn: async () => {
      const updatedData = {
        difficulty: "2"
      }
      return await fetch(`${apiUrl}/vocabs/${id}/update`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      })
    },
    onError: (error, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
    }
  })

  const handleSetAdv = (updatedData) => {
    setAdvMutation.mutate(updatedData)
  }

  const setAdvMutation = useMutation({
    mutationFn: async () => {
      const updatedData = {
        difficulty: "3"
      }
      return await fetch(`${apiUrl}/vocabs/${id}/update`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      })
    },
    onError: (error, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
    }
  })

  const handleSetNat = (updatedData) => {
    setNatMutation.mutate(updatedData)
  }

  const setNatMutation = useMutation({
    mutationFn: async () => {
      const updatedData = {
        difficulty: "4"
      }
      return await fetch(`${apiUrl}/vocabs/${id}/update`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      })
    },
    onError: (error, context) => {
      console.log("Error fetching:" + context.id + error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["allVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["pinnedVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["intVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["advVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["natVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["searchVocabsData"] })
      queryClient.invalidateQueries({ queryKey: ["labeledVocabsData"] })
    }
  })

  return (
    <>
      {isLandscape ? (
        <Stack direction={"row"}>
          <RowBox
            key={refreshKey}
            display="flex"
            flexDirection="row"
            onClick={handleViewOpen}
            gap={1}
            sx={{
              cursor: "pointer",
              borderRadius: "0.75rem",
              padding: "0.125rem 1rem",
              backgroundImage: highlightRow ? `linear-gradient(
                to right, 
                rgba(155, 155, 171, 0.04), 
                rgba(155, 155, 171, 0.1)
              )` : "none",
              "&:hover": {
                backgroundImage: `linear-gradient(
                  to right, 
                  rgba(155, 155, 171, 0.04), 
                  rgba(155, 155, 171, 0.1)
                )`,
              }
            }}
          >
            <Stack direction="column" spacing={0} pt={0.5} pb={0.5} onClick={handleViewOpen}>
              <Stack direction="row" alignItems={"center"} spacing={1}>
                <Typography
                  color={(difficulty === "1" && mode === "light") ? "#00b39b" :
                    (difficulty === "2" && mode === "light") ? "#d97706" :
                      difficulty === "3" ? "#ff589e" :
                        difficulty === "1" ? "#03f1c7" :
                          difficulty === "2" ? "#fbbf24" :
                            "#d934eb"}
                  fontSize={isWideScreens ? "2.5rem" : isQHDScreens ? "2rem" : "1.5rem"}
                  lineHeight={1.1}
                  fontWeight={mode === "light" ? 600 : 400}
                >
                  {text}
                </Typography>
                <Typography
                  fontSize={isWideScreens ? "1.5rem" : isQHDScreens ? "1.25rem" : "1rem"}
                  color={theme.palette.neutral.medium}
                  fontStyle={"italic"} lineHeight={1.1}
                >
                  {pinyin}
                </Typography>
              </Stack>
              <Typography fontSize={isWideScreens ? "1.5rem" : isQHDScreens ? "1.25rem" : "1rem"} fontWeight={mode === "light" ? 500 : 400}>
                <HighlightCaps text={definition} />
              </Typography>
            </Stack>

          </RowBox>
          <Tooltip title="Quick Edit" placement="right">
            <IconButton zindex={10} onClick={handleMenuClick}
              sx={{
                opacity: 0.3,
                "&:hover": {
                  backgroundColor: "transparent",  // Disable hover background
                },
              }}>
              <IoMdMore size={24} style={{ margin: "0 0.75rem" }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ) :
        /* Mobile */
        (
          <Stack direction={"row"}>
            <RowBox
              key={refreshKey}
              onClick={handleViewOpen}
              display="flex"
              flexDirection="row"
              gap={1}
              sx={{
                cursor: "pointer",
                borderRadius: "0.75rem",
                padding: "0.25rem",
                backgroundImage: highlightRow ? `linear-gradient(
                  to right, 
                  rgba(155, 155, 171, 0.04), 
                  rgba(155, 155, 171, 0.1)
                )` : "none",
                "&:hover": {
                  backgroundImage: `linear-gradient(
                    to right, 
                    rgba(155, 155, 171, 0.04), 
                    rgba(155, 155, 171, 0.1)
                  )`,
                }
              }}
            >
              <Stack direction="column" spacing={0}>
                <Stack direction="row" alignItems={"center"} spacing={0.5}>
                  <Typography
                    onClick={handleViewOpen}
                    sx={{ lineHeight: 1.1 }}
                    fontSize="1.3rem"
                    color={(difficulty === "1" && mode === "light") ? "#00b39b" :
                      (difficulty === "2" && mode === "light") ? "#d97706" :
                        difficulty === "3" ? "#ff589e" :
                          difficulty === "1" ? "#03f1c7" :
                            difficulty === "2" ? "#fbbf24" :
                              "#d934eb"}
                    fontWeight={mode === "light" ? 700 : 400}
                  >
                    {text}
                  </Typography>
                  <Typography
                    onClick={handleViewOpen}
                    sx={{ lineHeight: 1.1, fontSize: "0.8rem", fontStyle: "italic" }}
                    fontWeight={mode === "light" ? 500 : 400}
                    color={theme.palette.neutral.medium}>
                    {pinyin}
                  </Typography>
                </Stack>

                <Typography onClick={handleViewOpen} sx={{ lineHeight: 1.1 }} fontSize="1rem">
                  <HighlightCaps text={definition} />
                </Typography>
              </Stack>

            </RowBox>
            <IconButton
              zindex={10} onClick={handleMenuClick}
              sx={{
                opacity: 0.3, "&:hover": {
                  backgroundColor: "transparent",  // Disable hover background
                },
              }}>
              <IoMdMore />
            </IconButton>
          </Stack >
        )}

      {/* ----- Popup Delete Vocab Dialog ----- */}
      <Dialog
        open={deleteOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleDeleteClose}
        aria-describedby="alert-dialog-grow-description"
        sx={{
          "& .MuiDialog-paper": {
            width: isLandscape ? "18%" : "100%",
            padding: "1rem",
            borderRadius: "1rem",
            display: "flex",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
            backgroundColor: mode === "light" ? "rgba(255, 255, 255, 1)" : "rgba(0, 11, 13, 0.3)", // Semi-transparent background
            backgroundImage: `linear-gradient(
              to top left, 
              rgba(255, 255, 255, 0.15), 
              rgba(255, 255, 255, 0.05)
            )`, // Gradient overlay
            backdropFilter: "blur(6px)", // Apply the glass effect
            WebkitBackdropFilter: "blur(6px)", // For Safari support
            border: "1px solid rgba(255, 255, 255, 0.2)"
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 11, 13, 0.7)", // Custom backdrop color
            },
          },
        }}
      >
        <DeleteVocabDialog id={id} text={text} handleDeleteClose={handleDeleteClose} />
      </Dialog>

      {/* ----- Popup View Vocab Dialog ----- */}
      <Dialog
        open={vocabOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleViewClose}
        aria-describedby="alert-dialog-grow-description"
        sx={{
          "& .MuiDialog-paper": {
            position: "relative", // Required for pseudo-elements to work
            width: isLandscape ? "20%" : "100%",
            borderRadius: "1rem",
            display: "flex",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)", // Subtle shadow for depth
            backgroundColor: mode === "light" ? "rgba(255, 255, 255, 1)" : "rgba(0, 11, 13, 0.3)", // Semi-transparent background
            backgroundImage: mode === "light" ?
              `linear-gradient(
        to top left, 
        rgba(255,255,255, 0.15), 
        rgba(255,255,255, 0.05)
      )`
              :
              `linear-gradient(
        to top left, 
        rgba(170, 170, 193, 0.1), 
        rgba(170, 170, 193, 0.25), 
        rgba(170, 170, 193, 0.05)
      )`,
            backdropFilter: "blur(6px)", // Apply the glass effect
            WebkitBackdropFilter: "blur(6px)", // For Safari support
            border: "1px solid rgba(170, 170, 193, 0.35)"
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 11, 13, 0.7)", // Custom backdrop color
            },
          },
        }}
      >
        <Suspense fallback={
          <GridLoader
            color={theme.palette.neutral.light}
            loading={true}
            size={50}
            margin={20}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        }>
          <ViewVocabDialog
            viewVocab={viewVocab}
            viewUsage={viewUsage}
            setViewVocab={setViewVocab}
            setViewUsage={setViewUsage}
            handleViewClose={handleViewClose}
            id={id}
            text={text}
            pinyinText={pinyin}
            label={label}
            difficulty={difficulty}
            definition={definition}
            similar={similar}
            expression={expression}
            sentence={sentence}
            pinned={pinned}
          />
        </Suspense>
      </Dialog>
      <Menu
        id="basic-menu"
        anchorEl={menuAnchor}
        open={openMenu}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: mode === "light" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 11, 13, 0.3)", // Semi-transparent background
            backgroundImage: mode === "light" ?
              `linear-gradient(
        to bottom right, 
        rgba(255,255,255, 0.15), 
        rgba(255,255,255, 0.05)
      )`
              :
              `linear-gradient(
        to bottom right, 
        rgba(170, 170, 193, 0.2), 
        rgba(170, 170, 193, 0.05)
      )`,
            backdropFilter: "blur(6px)", // Apply the blur effect
            WebkitBackdropFilter: "blur(6px)", // Safari support for blur effect
            borderRadius: "1rem",
            boxShadow: mode === "light" ? "0px 4px 12px rgba(155, 155, 171, 0.4)" : "0px 4px 12px rgba(0, 11, 13, 0.4)",
            border: "1px solid rgba(255, 255, 255, 0.2)", // Optional border for frosted effect
          },
        }}
      >
        <MenuItem onClick={handleUpdatePinned}>
          <ListItemIcon><TbPin fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>{pinned ? "Unpin" : "Pin"}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetBeg}>
          <ListItemIcon><PiCircleBold fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>Set Lv. 1</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetInt}>
          <ListItemIcon><PiDiamondBold fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>Set Lv. 2</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetAdv}>
          <ListItemIcon><PiStarBold fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>Set Lv. 3</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSetNat}>
          <ListItemIcon><BsBookmarkStar fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>Set Lv. 4</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteOpen}>
          <ListItemIcon><TbTrashX fontSize="large" color={theme.palette.neutral.darker} /></ListItemIcon>
          <ListItemText sx={{ color: theme.palette.neutral.darker, fontWeight: mode === "light" ? 500 : 400 }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

export default PinnedVocabRow